import { Injectable } from '@angular/core';
import { ScoreResponse } from '../../../models';

/**
 * Pure utility functions for scorecard operations.
 * These functions have no side effects and are easily testable.
 */
@Injectable({
  providedIn: 'root'
})
export class ScorecardUtilitiesService {

  /**
   * Checks if a hole requires fairway tracking (par > 3)
   * @param par The par value for the hole
   * @returns True if fairway should be tracked
   */
  shouldTrackFairway(par: number): boolean {
    return par > 3;
  }

  /**
   * Formats display values for Y/N inputs
   * @param value Boolean value to format
   * @param showEmpty Whether to show empty string for false values on par 3s
   * @returns 'Y', 'N', or empty string
   */
  formatYesNoDisplay(value: boolean | undefined, showEmpty: boolean = false): string {
    if (showEmpty && !value) return '';
    return value ? 'Y' : 'N';
  }

  /**
   * Counts dirty scores across all scorecard collections
   * @param scoresCollections Object containing scorecard IDs mapped to score arrays
   * @returns Total number of dirty scores
   */
  countDirtyScores(scoresCollections: { [scorecardId: string]: ScoreResponse[] }): number {
    return Object.values(scoresCollections).reduce(
      (acc, scores) =>
        acc +
        scores.filter(
          (s) =>
            s.isDirty === true ||
            s.isDriveDistanceDirty === true ||
            s.isFairwayDirty === true ||
            s.isGirDirty === true ||
            s.isPuttsDirty === true ||
            s.isSandSaveDirty === true
        ).length,
      0
    );
  }

  /**
   * Validates that input contains only numeric characters
   * @param event Keyboard event
   * @returns True if character should be allowed
   */
  isNumericInput(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  /**
   * Sanitizes input to contain only numbers and prevents invalid scores
   * @param value Input string to sanitize
   * @returns Sanitized numeric string (excludes 0 as it's an invalid score)
   */
  sanitizeNumericInput(value: string): string {
    const NUMBER_REGEX = /[^0-9]/g;
    let sanitized = value.replace(NUMBER_REGEX, '');
    
    // Prevent standalone "0" from being entered as it's an invalid golf score
    // Valid scores are 1-99 (10, 20, etc. are valid, but not 0)
    if (sanitized === '0') {
      return '';
    }
    
    return sanitized;
  }

  /**
   * Validates that a score is valid for golf scoring
   * @param score The score value to validate
   * @returns True if the score is valid, false otherwise
   */
  isValidScore(score: number | string | null | undefined): boolean {
    if (score === null || score === undefined || score === '') {
      return true; // Empty scores are allowed (not yet entered)
    }
    
    const numericScore = typeof score === 'string' ? parseInt(score, 10) : score;
    
    // 0 is not a valid golf score
    // Valid scores are typically 1-99
    return !isNaN(numericScore) && numericScore > 0 && numericScore <= 99;
  }

  /**
   * Sanitizes Y/N input and converts 1/0 to Y/N
   * @param value Input string to sanitize
   * @returns Sanitized Y/N string
   */
  sanitizeYesNoInput(value: string): string {
    const YN_REGEX = /[^YNyn10]/g;
    return value
      .replace(YN_REGEX, '')
      .replace(/1/g, 'Y')
      .replace(/0/g, 'N')
      .toUpperCase();
  }

  /**
   * Checks if drive distance is measured for a specific hole
   * @param holeId ID of the hole to check
   * @param holes Array of holes to search in
   * @returns True if drive is measured for this hole, false otherwise
   */
  isDriveMeasuredForHole(holeId: string, holes: any[]): boolean {
    return holes.find((h) => h.holeId === holeId)?.isDriveMeasured ?? false;
  }

  /**
   * Calculates total for a set of scores with fallback display
   * @param scores Array of score responses
   * @param calculationService Service to perform the calculation
   * @returns Calculated total or '-' if no valid total
   */
  calculateScoresTotalWithFallback(scores: any[], calculationService: any): number | string {
    const total = calculationService.calculateScoresTotal(scores);
    return total ? total : '-';
  }

  /**
   * Calculates tab index for scorecard input navigation
   * @param scorecardMode Current scorecard mode ('scores', 'verify', etc.)
   * @param scoringType Current scoring type ('scorecard', 'hole')
   * @param rowId Player ID or row identifier
   * @param holeNumber Hole number (1-18)
   * @param scorecards Array of scorecards for player index calculation
   * @param holesPerRound Number of holes per round (typically 18)
   * @returns Calculated tab index or 0 for non-scores mode
   */
  calculateTabIndex(
    scorecardMode: string,
    scoringType: string,
    rowId: string,
    holeNumber: number,
    scorecards: any[],
    holesPerRound: number = 18
  ): number {
    if (scorecardMode !== 'scores') {
      return 0;
    }

    const playerIndex = scorecards.findIndex((p) => p.playerId === rowId);

    if (scoringType === 'scorecard') {
      const playerBase = playerIndex * holesPerRound;
      return playerBase + holeNumber;
    } else {
      const holeBase = (holeNumber - 1) * scorecards.length;
      return holeBase + playerIndex + 1;
    }
  }

  /**
   * Calculates verify scores total based on type and missing score validation
   * @param type Type of total to calculate ('IN', 'OUT', 'RND', 'TOTAL')
   * @param verifyScoresOUT Array of OUT verify scores
   * @param verifyScoresIN Array of IN verify scores
   * @param startScore Starting score for TOTAL calculation
   * @returns Calculated total or empty string if incomplete
   */
  calculateVerifyScoresTotal(
    type: string,
    verifyScoresOUT: any[],
    verifyScoresIN: any[],
    startScore?: number
  ): number | string {
    // Check for missing scores
    const missingOUTScores = verifyScoresOUT.some((score) => score.score === null);
    const missingINScores = verifyScoresIN.some((score) => score.score === null);

    const inTotal = verifyScoresIN.reduce((acc, score) => acc + (score.score || 0), 0);
    const outTotal = verifyScoresOUT.reduce((acc, score) => acc + (score.score || 0), 0);

    switch (type) {
      case 'IN':
        return missingINScores ? '' : inTotal;
      case 'OUT':
        return missingOUTScores ? '' : outTotal;
      case 'RND':
        return !missingINScores && !missingOUTScores ? inTotal + outTotal : '';
      case 'TOTAL':
        return !missingINScores && !missingOUTScores ? inTotal + outTotal + (startScore || 0) : '';
      default:
        return '';
    }
  }

  /**
   * Formats plus/minus display value
   * Pure function for display formatting
   */
  formatPlusMinus(plusMinus: number | undefined): string {
    if (plusMinus === undefined) return '-';
    if (plusMinus === 0) return 'E';
    if (plusMinus > 0) return `+${plusMinus}`;
    return `${plusMinus}`;
  }

  /**
   * Finds first empty input element from a list of inputs
   * Pure function for UI element location
   */
  findFirstEmptyInput(inputs: any[]): any | undefined {
    return inputs.find((input) => {
      const element = input.nativeElement as HTMLInputElement;
      return (
        element instanceof HTMLInputElement &&
        !element.value?.trim() &&
        !element.disabled &&
        element.offsetParent !== null &&
        element.getBoundingClientRect().height > 0
      );
    });
  }

  /**
   * Finds first invalid input based on verify score validation
   * Pure function for UI element location based on validation state
   */
  findFirstInvalidInput(
    inputs: any[],
    verifyScoresOUT: any[],
    verifyScoresIN: any[]
  ): any | undefined {
    const firstInvalidVerifyHole =
      verifyScoresOUT.find((score) => score.valid === false)?.holeNumber ??
      verifyScoresIN.find((score) => score.valid === false)?.holeNumber;

    if (firstInvalidVerifyHole !== undefined) {
      return inputs.find((input) => {
        const element = input.nativeElement as HTMLInputElement;
        return (
          element instanceof HTMLInputElement &&
          Number(element.getAttribute('data-hole-number')) === firstInvalidVerifyHole &&
          !element.disabled &&
          element.offsetParent !== null &&
          element.getBoundingClientRect().height > 0
        );
      });
    }

    // Fallback: find first empty and enabled input
    return this.findFirstEmptyInput(inputs);
  }

  /**
   * Creates range of verify scores for hole numbers
   * Pure function for data structure creation
   */
  createVerifyScores(startHole: number, endHole: number): any[] {
    return Array.from({ length: endHole - startHole + 1 }, (_, i) => ({
      holeNumber: startHole + i,
      score: null,
      valid: undefined,
    }));
  }

  /**
   * Filters holes into front nine and back nine arrays
   * Pure function for data organization
   */
  separateHolesByNine(holes: any[]): { holesOUT: any[]; holesIN: any[] } {
    const holesOUT = holes.filter(
      (hole) => hole.number !== undefined && hole.number <= 9
    );
    const holesIN = holes.filter(
      (hole) => hole.number !== undefined && hole.number > 9
    );
    
    return { holesOUT, holesIN };
  }

  /**
   * Maps score response objects to include formatted display values
   * Pure function for data transformation
   */
  mapScoresToDisplayFormat(
    scores: ScoreResponse[],
    shouldTrackFairway: (holeId: string) => boolean,
    isDriveMeasured: (holeId: string) => boolean
  ): ScoreResponse[] {
    return scores.map((score) => {
      const originalScore = score.score;
      const originalStatusCode = score.statusCode;
      const originalDriveDistance = score.driveDistance;
      const originalPutts = score.putts;
      const originalFairway = score.fairway;
      const originalSandSave = score.sandSaveMade;
      const originalGir = score.gir;

      const hasFairway = shouldTrackFairway(score.holeId || '');
      const isDriveMeasuredForHole = isDriveMeasured(score.holeId || '');

      return {
        ...score,
        originalStatusCode,
        originalScore,
        originalDriveDistance,
        originalPutts,
        originalFairway,
        originalSandSave,
        originalGir,
        driveDistanceDisplay: isDriveMeasuredForHole
          ? Number(score.driveDistance || 0)
          : '',
        fairwayDisplay: hasFairway ? (score.fairway ? 'Y' : 'N') : '',
        sandSaveMadeDisplay: score.sandSaveAttempted
          ? score.sandSaveMade
            ? 'Y'
            : 'N'
          : '',
        girDisplay: score.gir ? 'Y' : 'N',
        isDirty: false,
      };
    });
  }

  /**
   * Determines if score input should be visible based on verify score completion
   * Pure function for UI visibility logic
   */
  isScoreInputVisible(
    score: ScoreResponse,
    showAllScores: boolean,
    verifyScoresOUT: any[],
    verifyScoresIN: any[]
  ): boolean {
    if (showAllScores) return true;
    
    const verifyScoreOUT = verifyScoresOUT.find(
      (sc) => sc.holeNumber === score.holeNumber
    );
    const verifyScoreIN = verifyScoresIN.find(
      (sc) => sc.holeNumber === score.holeNumber
    );
    
    return verifyScoreOUT?.score !== null && verifyScoreIN?.score !== null;
  }

  /**
   * Determines if score totals should be visible based on completion status
   * Pure function for UI visibility logic
   */
  isScoreTotalVisible(
    direction: string,
    showAllScores: boolean,
    verifyScoresOUT: any[],
    verifyScoresIN: any[]
  ): boolean {
    if (showAllScores) return true;

    const areScoresCompleteOUT = verifyScoresOUT.every((sc) => sc.score !== null);
    const areScoresCompleteIN = verifyScoresIN.every((sc) => sc.score !== null);

    switch (direction.toLowerCase()) {
      case 'in':
        return areScoresCompleteIN;
      case 'out':
        return areScoresCompleteOUT;
      case 'rnd':
      case 'tot':
        return areScoresCompleteIN && areScoresCompleteOUT;
      default:
        return false;
    }
  }

  /**
   * Maps scorecard to display format with formatted plus/minus values
   * Pure function for data transformation
   */
  mapScorecardToDisplayFormat(scorecard: any): any {
    if (!scorecard) return scorecard;
    
    return {
      ...scorecard,
      startPlusMinusDisplay: this.formatPlusMinus(scorecard.startPlusMinus),
      finishPlusMinusDisplay: this.formatPlusMinus(scorecard.finishPlusMinus),
      roundPlusMinusDisplay: this.formatPlusMinus(scorecard.roundPlusMinus),
      isDirty: false,
    };
  }

  /**
   * Checks if any scores are dirty for a given score array
   * @param scores Array of scores to check
   * @returns True if any scores are dirty
   */
  private isAnyTotalDirty(scores: ScoreResponse[] | null): boolean {
    if (!scores) return false;
    return scores.some(score => score.isDirty);
  }

  /**
   * Checks if any OUT scores are dirty for a given scorecard
   * @param scores Array of OUT scores to check
   * @returns True if any OUT scores are dirty
   */
  isOUTTotalDirty(scores: ScoreResponse[]): boolean {
    return this.isAnyTotalDirty(scores);
  }

  /**
   * Checks if any IN scores are dirty for a given scorecard
   * @param scores Array of IN scores to check
   * @returns True if any IN scores are dirty
   */
  isINTotalDirty(scores: ScoreResponse[]): boolean {
    return this.isAnyTotalDirty(scores);
  }

  /**
   * Checks if any scores in the round are dirty (OUT or IN)
   * @param scoresOUT Array of OUT scores
   * @param scoresIN Array of IN scores
   * @returns True if any scores in the round are dirty
   */
  isRoundTotalDirty(scoresOUT: ScoreResponse[], scoresIN: ScoreResponse[]): boolean {
    return this.isOUTTotalDirty(scoresOUT) || this.isINTotalDirty(scoresIN);
  }

  /**
   * Checks if any scores are dirty (alias for round total)
   * @param scoresOUT Array of OUT scores
   * @param scoresIN Array of IN scores
   * @returns True if any scores are dirty
   */
  isTotalDirty(scoresOUT: ScoreResponse[], scoresIN: ScoreResponse[]): boolean {
    return this.isRoundTotalDirty(scoresOUT, scoresIN);
  }
}
